import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { EditorAIAnalyticEventPayload } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import type { InitAction } from '../stream-processors/partial-stream-parser';

export type UploadAndInsertMediaErrorPayload = {
	action: 'error';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'uploadAndInsertMedia';
	attributes: {
		conversationChannelId?: string;
		errorFlagShown?: boolean;
		errorMessage: string;
		experienceName: string | null;
		selectionCategory: null;
		selectionType: null;
		source: { isCreateAction: false; type: 'orchestrator' };
		streamId: null;
	};
	eventType: typeof EVENT_TYPE.OPERATIONAL;
};

export const createUploadAndInsertMediaErrorPayload = ({
	errorMessage,
	conversationChannelId,
	errorFlagShown,
}: {
	conversationChannelId?: string;
	errorFlagShown?: boolean;
	errorMessage: string;
}): UploadAndInsertMediaErrorPayload => ({
	action: 'error',
	actionSubject: 'editorPluginAI',
	actionSubjectId: 'uploadAndInsertMedia',
	eventType: EVENT_TYPE.OPERATIONAL,
	attributes: {
		errorMessage,
		...(conversationChannelId ? { conversationChannelId } : {}),
		...(errorFlagShown !== undefined ? { errorFlagShown } : {}),
		experienceName: null,
		selectionType: null,
		selectionCategory: null,
		streamId: null,
		source: { type: 'orchestrator', isCreateAction: false },
	},
});

export function fireUploadAndInsertMediaErrorAnalyticsEvent({
	editorApi,
	errorMessage,
	conversationChannelId,
	errorFlagShown,
}: {
	conversationChannelId?: string;
	editorApi: PublicPluginAPI<[AnalyticsPlugin]>;
	errorFlagShown?: boolean;
	errorMessage: string;
}): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<
		EditorAIAnalyticEventPayload | UploadAndInsertMediaErrorPayload,
		'customEventType'
	>(
		createUploadAndInsertMediaErrorPayload({ errorMessage, conversationChannelId, errorFlagShown }),
	);
}

export type StaleMediaCollectionPayload = {
	action: 'staleCollectionDetected';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'uploadAndInsertMedia';
	attributes: {
		currentContentId: string;
		frozenCollection: string;
	};
	eventType: typeof EVENT_TYPE.OPERATIONAL;
};

export const createStaleMediaCollectionPayload = (
	frozenCollection: string,
	currentContentId: string,
): StaleMediaCollectionPayload => ({
	action: 'staleCollectionDetected',
	actionSubject: 'editorPluginAI',
	actionSubjectId: 'uploadAndInsertMedia',
	eventType: EVENT_TYPE.OPERATIONAL,
	attributes: {
		frozenCollection,
		currentContentId,
	},
});

export function fireStaleMediaCollectionAnalyticsEvent(
	editorApi: PublicPluginAPI<[AnalyticsPlugin]>,
	frozenCollection: string,
	currentContentId: string,
): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<
		EditorAIAnalyticEventPayload | StaleMediaCollectionPayload,
		'customEventType'
	>(createStaleMediaCollectionPayload(frozenCollection, currentContentId));
}

export function fireInfiniteLoopDetectedAnalyticsEvent(
	editorApi: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
	result: InitAction,
): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			errorMessage: 'Infinite loop detected',
			errorData: {
				action: result.name,
				localId: result.localId,
			},
			experienceName: null,
			selectionType: null,
			selectionCategory: null,
			streamId: null,
			source: { type: 'orchestrator', isCreateAction: false },
		},
	});
}

/** Per-event attributes for `parallelToolCall.forceFinished`. */
export type ParallelToolCallForceFinishedAttributes = {
	completedAfterTimeout: boolean;
	envelopeId: string;
	firstLateChunkDelayMs: number | null;
	lastLateChunkDelayMs: number | null;
	lateChunkCount: number;
	timeoutMs: number;
	/** The `name` field from the originating v2 envelope (e.g. `'appendNodeToEnd'`). */
	toolCallName: string;
};

/** Analytics payload for each v2 tool-call force-finished by the FIFO queue timeout. */
export type ParallelToolCallForceFinishedPayload = {
	action: 'forceFinished';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'parallelToolCall';
	attributes: ParallelToolCallForceFinishedAttributes;
	eventType: typeof EVENT_TYPE.OPERATIONAL;
};

export const createParallelToolCallForceFinishedPayload = (
	attributes: ParallelToolCallForceFinishedAttributes,
): ParallelToolCallForceFinishedPayload => ({
	action: 'forceFinished',
	actionSubject: 'editorPluginAI',
	actionSubjectId: 'parallelToolCall',
	eventType: EVENT_TYPE.OPERATIONAL,
	attributes,
});

export function fireParallelToolCallForceFinishedAnalyticsEvent(
	editorApi: PublicPluginAPI<[AnalyticsPlugin]>,
	attributes: ParallelToolCallForceFinishedAttributes,
): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<
		EditorAIAnalyticEventPayload | ParallelToolCallForceFinishedPayload,
		'customEventType'
	>(createParallelToolCallForceFinishedPayload(attributes));
}
